import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { SuburbDataService, SuburbDraft } from '../../../../services/suburb-data.service';
import { apiErrorText } from '../workspace/api-errors';

/**
 * Lead and bio drafts for a suburb's page (#254; plan §7.2): written by the drafts CLI (Bedrock, from the suburb's
 * facts pack), shown beside the page's live intro and body. Accept takes the lead, the bio or both (edited here first
 * if need be); Discard drops the draft. Nothing reaches the website until it's accepted, and then only when the site is
 * next published. Drafts are text: interpolated only.
 */
@Component({
  selector: 'app-suburb-drafts',
  templateUrl: './suburb-drafts.component.html',
  styleUrls: ['./suburb-drafts.component.scss'],
})
export class SuburbDraftsComponent implements OnChanges {
  @Input() salCode!: string;
  @Input() readOnly = false;
  /** The page's live intro and body, to show beside the draft. */
  @Input() liveIntro: string | null = null;
  @Input() liveBody: string | null = null;
  /** The accepted text, for the workspace to update its copy of the page record. */
  @Output() accepted = new EventEmitter<{ intro?: string; body?: string }>();

  drafts: SuburbDraft[] = [];
  loading = false;
  busy = false;
  error = '';
  message = '';
  /** The draft being reviewed, with its text as edited. */
  editing: { id: number; lead: string; bio: string; useLead: boolean; useBio: boolean } | null =
    null;

  constructor(
    private data: SuburbDataService,
    private zone: NgZone,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    // Only a different suburb starts over: the live intro/body change when a draft is accepted, and that mustn't
    // wipe the "Accepted" message.
    if (!changes.salCode) return;
    this.editing = null;
    this.message = '';
    this.load();
  }

  get waiting(): SuburbDraft[] {
    return this.drafts.filter((d) => d.status === 'draft');
  }

  get decided(): SuburbDraft[] {
    return this.drafts.filter((d) => d.status !== 'draft');
  }

  private fail(e: unknown): string {
    return apiErrorText(e, 'something went wrong');
  }

  private refresh(): void {
    this.cdr.detectChanges();
  }

  load(): void {
    if (!this.salCode) return;
    this.loading = true;
    this.error = '';
    this.data.drafts(this.salCode).subscribe({
      next: (d) => this.zone.run(() => ((this.drafts = d), (this.loading = false), this.refresh())),
      error: (e) =>
        this.zone.run(() => ((this.error = this.fail(e)), (this.loading = false), this.refresh())),
    });
  }

  review(d: SuburbDraft): void {
    this.editing = {
      id: d.id,
      lead: d.lead ?? '',
      bio: d.bio ?? '',
      useLead: !!d.lead,
      useBio: !!d.bio,
    };
    this.message = '';
  }

  accept(): void {
    const e = this.editing;
    if (!e || this.busy || (!e.useLead && !e.useBio)) return;
    this.busy = true;
    this.data
      .acceptDraft(e.id, {
        lead: e.useLead ? e.lead : null,
        bio: e.useBio ? e.bio : null,
        use: { lead: e.useLead, bio: e.useBio },
      })
      .subscribe({
        next: (r) =>
          this.zone.run(() => {
            this.busy = false;
            this.editing = null;
            this.message = `Accepted: the page's ${[r.intro !== undefined ? 'intro' : '', r.body !== undefined ? 'body' : ''].filter(Boolean).join(' and ')} now ${r.intro !== undefined && r.body !== undefined ? 'are' : 'is'} the draft's. Publish the website to put it live.`;
            this.accepted.emit(r);
            this.load();
          }),
        error: (err) =>
          this.zone.run(() => ((this.busy = false), (this.error = this.fail(err)), this.refresh())),
      });
  }

  discard(d: SuburbDraft): void {
    if (this.busy) return;
    this.busy = true;
    this.data.discardDraft(d.id).subscribe({
      next: () =>
        this.zone.run(
          () => (
            (this.busy = false),
            (this.editing = null),
            (this.message = 'Discarded.'),
            this.load()
          )
        ),
      error: (err) =>
        this.zone.run(() => ((this.busy = false), (this.error = this.fail(err)), this.refresh())),
    });
  }

  words(s: string | null): number {
    return s ? s.trim().split(/\s+/).filter(Boolean).length : 0;
  }
}
